import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DatePickerField from "@/components/DatePickerField";
import ProductSelectorDialog from "@/components/ProductSelectorDialog";
import { calculateEventDays, formatCurrency, formatDateRange } from "@/lib/formatters";
import * as db from "@/lib/db";

const makeId = () => crypto.randomUUID();
const num = (value) => parseFloat(value) || 0;
const emptyStaff = () => ({ tempId: makeId(), staffName: "", wageAmount: "", remark: "" });

const AddEvent = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(eventId);

  const [eventName, setEventName] = useState("");
  const [activityTypeId, setActivityTypeId] = useState("");
  const [activityTypes, setActivityTypes] = useState([]);
  const [status, setStatus] = useState("completed");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [generalNotes, setGeneralNotes] = useState("");

  const [revenueType, setRevenueType] = useState("product_sales");
  const [productSalesCollected, setProductSalesCollected] = useState("");
  const [otherRevenueAmount, setOtherRevenueAmount] = useState("");
  const [otherRevenueRemark, setOtherRevenueRemark] = useState("");
  const [standardCupSellingPrice, setStandardCupSellingPrice] = useState("8.90");
  const [conservativeCupUnitCost, setConservativeCupUnitCost] = useState("1.38");
  const [settingsRemark, setSettingsRemark] = useState("");
  const [showSettings, setShowSettings] = useState(false);

  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);

  const [staffWages, setStaffWages] = useState([]);
  const [rentalCost, setRentalCost] = useState("");
  const [linkedPrepaidRentalTotal, setLinkedPrepaidRentalTotal] = useState(0);
  const [linkedPrepaidRentalTransactions, setLinkedPrepaidRentalTransactions] = useState([]);
  const [transportCost, setTransportCost] = useState("");
  const [otherCost, setOtherCost] = useState("");
  const [otherCostRemark, setOtherCostRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputClass = "bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base";
  const labelClass = "text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block";

  useEffect(() => {
    const load = async () => {
      try {
        const [productsData, categoriesData, settings, activityTypesData] = await Promise.all([
          db.listProducts(),
          db.listCategories(),
          db.getReconciliationSettings(),
          db.listActivityTypes(),
        ]);

        setAvailableProducts(productsData);
        setCategories(categoriesData);
        setActivityTypes(activityTypesData);

        const defaultAt = activityTypesData.find((a) => a.name.toLowerCase() === "bazaar / event");
        if (!eventId && defaultAt) setActivityTypeId(defaultAt.id);

        setStandardCupSellingPrice(String(settings.standardCupSellingPrice));
        setConservativeCupUnitCost(String(settings.conservativeCupUnitCost));
        setSettingsRemark(settings.remark || "");

        if (!eventId) return;
        const ev = await db.getEvent(eventId);
        const loadedRevenueType = ev.revenueType || "product_sales";

        setEventName(ev.eventName || "");
        setActivityTypeId(ev.activityTypeId || defaultAt?.id || "");
        setStatus(ev.status || "completed");
        setStartDate(ev.startDate || "");
        setEndDate(ev.endDate || "");
        setGeneralNotes(ev.generalNotes || "");
        setRevenueType(loadedRevenueType);
        setProductSalesCollected(
          ev.productSalesCollected != null
            ? String(ev.productSalesCollected)
            : loadedRevenueType === "product_sales" && ev.totalSalesCollected != null
            ? String(ev.totalSalesCollected)
            : ""
        );
        setOtherRevenueAmount(Number(ev.otherRevenueAmount || 0) === 0 ? "" : String(ev.otherRevenueAmount));
        setOtherRevenueRemark(ev.otherRevenueRemark || "");

        setProducts((ev.products || []).map((p) => ({
          ...p,
          tempId: makeId(),
          quantitySold: String(p.quantitySold ?? ""),
          totalSalesAmount: String(p.totalSalesAmount ?? ""),
          unitCost: String(p.unitCost ?? ""),
          remark: p.remark || "",
          defaultPrice: p.defaultPrice != null ? Number(p.defaultPrice) : null,
          salesAutoCalc: false,
        })));

        setStaffWages((ev.staffWages || []).map((s) => ({
          ...s,
          tempId: makeId(),
          wageAmount: String(s.wageAmount ?? ""),
          remark: s.remark || "",
        })));

        setRentalCost(ev.manualRentalCost ? String(ev.manualRentalCost) : "");
        setLinkedPrepaidRentalTotal(Number(ev.linkedPrepaidRentalCost || 0));
        setLinkedPrepaidRentalTransactions(ev.linkedPrepaidRentalTransactions || []);
        setTransportCost(ev.transportCost ? String(ev.transportCost) : "");
        setOtherCost(ev.otherCost ? String(ev.otherCost) : "");
        setOtherCostRemark(ev.otherCostRemark || "");

        if (ev.standardCupSellingPrice) setStandardCupSellingPrice(String(ev.standardCupSellingPrice));
        if (ev.conservativeCupUnitCost) setConservativeCupUnitCost(String(ev.conservativeCupUnitCost));
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data");
      }
    };

    load();
  }, [eventId]);

  const totalEventDays = useMemo(
    () => calculateEventDays(startDate, endDate),
    [startDate, endDate]
  );

  const productCalculations = useMemo(() => products.map((p) => {
    const qtyNum = num(p.quantitySold);
    const unitCostNum = num(p.unitCost);
    const salesNum = num(p.totalSalesAmount);
    const productCostCalc = qtyNum * unitCostNum;
    return {
      ...p,
      qtyNum,
      salesNum,
      productCostCalc,
      productGrossProfitCalc: salesNum - productCostCalc,
    };
  }), [products]);

  const totals = useMemo(() => {
    const totalRecordedProductSales = productCalculations.reduce((sum, p) => sum + p.salesNum, 0);
    const totalRecordedProductCost = productCalculations.reduce((sum, p) => sum + p.productCostCalc, 0);
    const totalQuantity = productCalculations.reduce((sum, p) => sum + p.qtyNum, 0);
    const totalStaff = staffWages.reduce((sum, s) => sum + num(s.wageAmount), 0);

    const manualRental = num(rentalCost);
    const linkedRental = Number(linkedPrepaidRentalTotal || 0);
    const rental = manualRental + linkedRental;
    const transport = num(transportCost);
    const other = num(otherCost);

    const productRevenue = revenueType === "other_revenue" ? 0 : num(productSalesCollected);
    const otherRevenue = revenueType === "product_sales" ? 0 : num(otherRevenueAmount);
    const totalRevenue = productRevenue + otherRevenue;

    const cupPrice = num(standardCupSellingPrice) || 8.90;
    const cupCost = num(conservativeCupUnitCost);
    const usesProductReconciliation = revenueType === "product_sales" || revenueType === "mixed";
    const unrecordedSales = usesProductReconciliation ? productRevenue - totalRecordedProductSales : 0;

    let rawUnrecordedCups = 0;
    let wholeCups = 0;
    let estimatedUnrecordedCogs = 0;

    if (usesProductReconciliation && unrecordedSales > 0 && cupPrice > 0) {
      rawUnrecordedCups = unrecordedSales / cupPrice;
      wholeCups = Math.ceil(rawUnrecordedCups);
      estimatedUnrecordedCogs = wholeCups * cupCost;
    }

    const totalCogs = totalRecordedProductCost + estimatedUnrecordedCogs;
    const grossProfit = totalRevenue - totalCogs;
    const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    const totalOpex = rental + totalStaff + transport + other;
    const netProfit = grossProfit - totalOpex;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const totalCost = totalCogs + totalOpex;
    const avgSales = totalEventDays > 0 ? totalRevenue / totalEventDays : 0;
    const avgNetProfit = totalEventDays > 0 ? netProfit / totalEventDays : 0;

    const baseTotalOpex = manualRental + totalStaff + transport + other;
    const baseNetProfit = grossProfit - baseTotalOpex;
    const baseProfitMargin = totalRevenue > 0 ? (baseNetProfit / totalRevenue) * 100 : 0;
    const baseTotalCost = totalCogs + baseTotalOpex;
    const baseAvgNetProfit = totalEventDays > 0 ? baseNetProfit / totalEventDays : 0;

    let reconciliationStatus = "Manual Review Required";
    if (revenueType === "other_revenue") {
      reconciliationStatus = "Not Applicable — Non-Product Revenue";
    } else if (productSalesCollected === "" || Number.isNaN(parseFloat(productSalesCollected))) {
      reconciliationStatus = "Manual Review Required";
    } else if (Math.abs(unrecordedSales) < 0.005) {
      reconciliationStatus = "Fully Reconciled";
    } else if (unrecordedSales > 0) {
      reconciliationStatus = "Unrecorded Cup Sales Estimated";
    } else {
      reconciliationStatus = "Recorded Product Sales Exceed Product Sales Collected";
    }

    return {
      productRevenue,
      otherRevenue,
      totalRevenue,
      totalRecordedProductSales,
      totalRecordedProductCost,
      totalQuantity,
      totalStaff,
      manualRental,
      linkedRental,
      rental,
      transport,
      other,
      unrecordedSales,
      rawUnrecordedCups,
      wholeCups,
      estimatedUnrecordedCogs,
      cupPrice,
      cupCost,
      totalCogs,
      grossProfit,
      grossMargin,
      totalOpex,
      netProfit,
      profitMargin,
      totalCost,
      avgSales,
      avgNetProfit,
      baseTotalOpex,
      baseNetProfit,
      baseProfitMargin,
      baseTotalCost,
      baseAvgNetProfit,
      reconciliationStatus,
    };
  }, [
    productCalculations,
    staffWages,
    rentalCost,
    linkedPrepaidRentalTotal,
    transportCost,
    otherCost,
    totalEventDays,
    revenueType,
    productSalesCollected,
    otherRevenueAmount,
    standardCupSellingPrice,
    conservativeCupUnitCost,
  ]);

  const addProduct = (product) => {
    const defaultPrice = product.defaultPrice != null ? Number(product.defaultPrice) : null;
    setProducts((prev) => [...prev, {
      tempId: makeId(),
      productId: product.id,
      productName: product.name,
      category: product.category,
      unit: product.unit,
      unitCost: String(product.unitCost),
      defaultPrice,
      salesAutoCalc: defaultPrice != null,
      quantitySold: "",
      totalSalesAmount: "",
      remark: "",
    }]);
  };

  const changeProduct = (tempId, field, value) => {
    setProducts((prev) => prev.map((p) => {
      if (p.tempId !== tempId) return p;
      const next = { ...p, [field]: value };
      if (field === "quantitySold" && next.salesAutoCalc && next.defaultPrice != null) {
        const qty = parseFloat(value);
        next.totalSalesAmount = Number.isNaN(qty) || qty <= 0 ? "0" : (qty * next.defaultPrice).toFixed(2);
      }
      if (field === "totalSalesAmount") next.salesAutoCalc = false;
      return next;
    }));
  };

  const resetAutoCalc = (tempId) => {
    setProducts((prev) => prev.map((p) => {
      if (p.tempId !== tempId || p.defaultPrice == null) return p;
      const qty = parseFloat(p.quantitySold);
      return {
        ...p,
        salesAutoCalc: true,
        totalSalesAmount: Number.isNaN(qty) || qty <= 0 ? "0" : (qty * p.defaultPrice).toFixed(2),
      };
    }));
  };

  const validate = () => {
    if (!eventName.trim()) return toast.error("Event name is required"), false;
    if (!activityTypeId) return toast.error("Activity Type is required"), false;
    if (!startDate) return toast.error("Start date is required"), false;
    if (!endDate) return toast.error("End date is required"), false;
    if (endDate < startDate) return toast.error("End date cannot be earlier than start date"), false;

    const usesProductRevenue = revenueType === "product_sales" || revenueType === "mixed";
    const usesOtherRevenue = revenueType === "other_revenue" || revenueType === "mixed";
    const productRevenueValue = productSalesCollected === "" ? null : parseFloat(productSalesCollected);
    const otherRevenueValue = otherRevenueAmount === "" ? null : parseFloat(otherRevenueAmount);

    if (status !== "draft") {
      if (usesProductRevenue && products.length === 0) {
        return toast.error(revenueType === "mixed" ? "At least one product must be added for Mixed Revenue" : "At least one product must be added"), false;
      }
      if (revenueType === "other_revenue" && products.length > 0) {
        return toast.error("Other / Non-Product Revenue cannot contain Products Sold. Remove the products or change Revenue Type to Mixed Revenue."), false;
      }
      if (usesProductRevenue && (productRevenueValue === null || Number.isNaN(productRevenueValue))) {
        return toast.error("Product Sales Collected is required"), false;
      }
      if (usesProductRevenue && productRevenueValue < 0) return toast.error("Product Sales Collected cannot be negative"), false;
      if (usesOtherRevenue && (otherRevenueValue === null || Number.isNaN(otherRevenueValue))) {
        return toast.error("Other / Non-Product Revenue amount is required"), false;
      }
      if (usesOtherRevenue && otherRevenueValue <= 0) return toast.error("Other / Non-Product Revenue amount must be greater than zero"), false;
    } else {
      if (productSalesCollected !== "" && (Number.isNaN(productRevenueValue) || productRevenueValue < 0)) {
        return toast.error("Product Sales Collected cannot be negative"), false;
      }
      if (otherRevenueAmount !== "" && (Number.isNaN(otherRevenueValue) || otherRevenueValue < 0)) {
        return toast.error("Other / Non-Product Revenue amount cannot be negative"), false;
      }
    }

    if (usesProductRevenue && num(standardCupSellingPrice) <= 0) return toast.error("Standard cup selling price must be greater than zero"), false;
    if (usesProductRevenue && num(conservativeCupUnitCost) < 0) return toast.error("Conservative cup unit cost cannot be negative"), false;

    for (const p of products) {
      const qty = parseFloat(p.quantitySold);
      const sales = parseFloat(p.totalSalesAmount);
      const cost = parseFloat(p.unitCost);
      if (Number.isNaN(qty) || qty < 0) return toast.error(`Quantity sold for ${p.productName} cannot be negative`), false;
      if (Number.isNaN(sales) || sales < 0) return toast.error(`Total sales for ${p.productName} cannot be negative`), false;
      if (Number.isNaN(cost) || cost < 0) return toast.error(`Unit cost for ${p.productName} cannot be negative`), false;
    }

    for (const s of staffWages) {
      if (s.wageAmount && !s.staffName.trim()) return toast.error("Staff name is required when wage amount is entered"), false;
      if (num(s.wageAmount) < 0) return toast.error("Staff wage cannot be negative"), false;
    }

    if (num(rentalCost) < 0) return toast.error("Rental cost cannot be negative"), false;
    if (num(transportCost) < 0) return toast.error("Transport cost cannot be negative"), false;
    if (num(otherCost) < 0) return toast.error("Other cost cannot be negative"), false;
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        eventName: eventName.trim(),
        activityTypeId: activityTypeId || null,
        status,
        startDate,
        endDate,
        totalEventDays,
        generalNotes: generalNotes.trim() || null,
        revenueType,
        productSalesCollected: totals.productRevenue,
        otherRevenueAmount: totals.otherRevenue,
        otherRevenueRemark: otherRevenueRemark.trim() || null,
        products: productCalculations.map((p) => ({
          productId: p.productId,
          productName: p.productName,
          category: p.category,
          unit: p.unit,
          unitCost: parseFloat(p.unitCost),
          defaultPrice: p.defaultPrice != null ? Number(p.defaultPrice) : null,
          quantitySold: p.qtyNum,
          totalSalesAmount: p.salesNum,
          productCost: p.productCostCalc,
          productGrossProfit: p.productGrossProfitCalc,
          remark: p.remark.trim() || null,
        })),
        staffWages: staffWages
          .filter((s) => s.staffName.trim() || s.wageAmount)
          .map((s) => ({ staffName: s.staffName.trim(), wageAmount: num(s.wageAmount), remark: s.remark.trim() || null })),
        rentalCost: totals.manualRental,
        transportCost: totals.transport,
        otherCost: totals.other,
        otherCostRemark: otherCostRemark.trim() || null,
        totalSalesCollected: totals.totalRevenue,
        totalRecordedProductSales: totals.totalRecordedProductSales,
        unrecordedSalesAmount: totals.unrecordedSales,
        rawUnrecordedCupEquivalent: totals.rawUnrecordedCups,
        estimatedUnrecordedWholeCups: totals.wholeCups,
        estimatedUnrecordedCogs: totals.estimatedUnrecordedCogs,
        standardCupSellingPrice: totals.cupPrice,
        conservativeCupUnitCost: totals.cupCost,
        roundingMethod: revenueType === "other_revenue" ? "Not applicable — non-product revenue" : "Rounded upward to the next whole cup",
        reconciliationStatus: totals.reconciliationStatus,
        totalCostOfGoodsSold: totals.totalCogs,
        totalSales: totals.totalRecordedProductSales,
        totalProductCost: totals.totalRecordedProductCost,
        totalProductGrossProfit: totals.totalRecordedProductSales - totals.totalRecordedProductCost,
        totalStaffWages: totals.totalStaff,
        totalCost: totals.baseTotalCost,
        netProfit: totals.baseNetProfit,
        profitMargin: totals.baseProfitMargin,
        totalProductQuantitySold: totals.totalQuantity,
        averageSalesPerDay: totals.avgSales,
        averageNetProfitPerDay: totals.baseAvgNetProfit,
      };

      if (revenueType !== "other_revenue") {
        try {
          await db.updateReconciliationSettings({
            standardCupSellingPrice: totals.cupPrice,
            conservativeCupUnitCost: totals.cupCost,
            remark: settingsRemark,
          });
        } catch (error) {
          console.warn("Could not save reconciliation settings", error);
          toast.warning("Reconciliation settings could not be saved — event saved without settings update.");
        }
      }

      if (isEditMode) {
        await db.updateEvent(eventId, payload);
        toast.success("Event updated successfully");
        navigate(`/events/${eventId}`);
      } else {
        await db.createEvent(payload);
        toast.success(status === "draft" ? "Draft event created successfully" : "Event saved successfully");
        navigate("/events");
      }
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.message || "Unable to save the record. Your entered information has been retained. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addActivityType = async () => {
    const name = window.prompt("New activity type name:");
    if (!name?.trim()) return;
    try {
      const created = await db.createActivityType(name.trim());
      setActivityTypes((prev) => [...prev, created]);
      setActivityTypeId(created.id);
      toast.success(`Added "${created.name}"`);
    } catch (error) {
      toast.error(error.message || "Could not add activity type");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {isEditMode ? "Edit Event" : "Add Event"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Record a new bazaar or event P&amp;L</p>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-5">
          <h2 className="text-lg font-medium text-slate-800">Event Information</h2>
          <Field label="Event / Activity Name *">
            <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Kepong Bazaar Weekend" className={inputClass} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Activity Type *">
              <select
                value={activityTypeId}
                onChange={(e) => e.target.value === "__add_new__" ? addActivityType() : setActivityTypeId(e.target.value)}
                className={`${inputClass} w-full appearance-none cursor-pointer`}
              >
                <option value="" disabled>Select activity type…</option>
                {activityTypes.map((at) => <option key={at.id} value={at.id}>{at.name}</option>)}
                <option value="__add_new__">+ Add new activity type…</option>
              </select>
            </Field>

            <Field label="Status">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setStatus("completed")} className={`h-12 rounded-xl border font-semibold text-sm ${status === "completed" ? "bg-[#193f7d] border-[#193f7d] text-white" : "bg-white border-slate-200 text-slate-600"}`}>Completed</button>
                <button type="button" onClick={() => setStatus("draft")} className={`h-12 rounded-xl border font-semibold text-sm ${status === "draft" ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600"}`}>Draft</button>
              </div>
              {status === "draft" && <p className="text-xs text-amber-700 mt-2">Draft events can be created before sales and products are known. This is useful when rental is paid in advance.</p>}
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Start Date *"><DatePickerField value={startDate} onChange={setStartDate} placeholder="Select start date" /></Field>
            <Field label="End Date *"><DatePickerField value={endDate} onChange={setEndDate} placeholder="Select end date" /></Field>
          </div>

          {startDate && endDate && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Event Duration</p>
              <p className="text-slate-900 mt-1">{formatDateRange(startDate, endDate)} · <span className="font-semibold">{totalEventDays} {totalEventDays === 1 ? "day" : "days"}</span></p>
            </div>
          )}

          <Field label="General Notes">
            <Textarea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} placeholder="Optional notes about this event..." className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-h-[80px]" />
          </Field>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-slate-800">Event Revenue</h2>
              <p className="text-xs text-slate-500 mt-1">Separate product sales from revenue that did not come from products.</p>
            </div>
            {revenueType !== "other_revenue" && (
              <button type="button" onClick={() => setShowSettings((v) => !v)} className="text-xs font-bold uppercase tracking-wider text-[#193f7d] hover:underline">
                {showSettings ? "Hide Settings" : "Reconciliation Settings"}
              </button>
            )}
          </div>

<Field label="Revenue Type *">

  <div className="relative">

    <select
      value={
        revenueType
      }
      onChange={(
        event
      ) => {
        setRevenueType(
          event.target.value
        );

        if (
          event.target.value ===
          "other_revenue"
        ) {
          setShowSettings(
            false
          );
        }
      }}
      className={`${inputClass} w-full appearance-none cursor-pointer pr-12`}
    >
      <option value="product_sales">
        Product Sales
      </option>

      <option value="other_revenue">
        Other / Non-Product Revenue
      </option>

      <option value="mixed">
        Mixed Revenue
      </option>
    </select>

    <ChevronDown
      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500"
    />

  </div>

  <p className="text-xs text-slate-500 mt-2">
    Product Sales uses cup reconciliation. Other / Non-Product Revenue bypasses cup estimation. Mixed Revenue keeps both revenue streams separate.
  </p>

</Field>

          {revenueType !== "other_revenue" && (
            <Field label={`Product Sales Collected (RM)${status === "completed" ? " *" : ""}`}>
              <Input type="number" step="0.01" min="0" value={productSalesCollected} onChange={(e) => setProductSalesCollected(e.target.value)} placeholder="0.00" className={inputClass} />
              <p className="text-xs text-slate-500 mt-2">Only money collected from product sales. This amount is compared against Products Sold for reconciliation.</p>
            </Field>
          )}

          {revenueType !== "product_sales" && (
            <>
              <Field label={`Other / Non-Product Revenue (RM)${status === "completed" ? " *" : ""}`}>
                <Input type="number" step="0.01" min="0" value={otherRevenueAmount} onChange={(e) => setOtherRevenueAmount(e.target.value)} placeholder="0.00" className={inputClass} />
                <p className="text-xs text-slate-500 mt-2">For equipment loan income, sponsorship, booth sharing, service fees, courtesy payments, or other income not generated by product sales.</p>
              </Field>
              <Field label="Other Revenue Remark">
                <Textarea value={otherRevenueRemark} onChange={(e) => setOtherRevenueRemark(e.target.value)} placeholder="e.g. Courtesy payment for machine loan" className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-h-[70px]" />
              </Field>
            </>
          )}

          <div className="border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Event Revenue</p>
              <p className="text-xs text-slate-500 mt-1">Product Sales + Other / Non-Product Revenue</p>
            </div>
            <p className="font-mono text-lg font-bold text-[#193f7d]">{formatCurrency(totals.totalRevenue)}</p>
          </div>

          {revenueType === "other_revenue" && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-blue-900">Revenue Classification: Non-Product Revenue</p>
              <p className="text-xs text-blue-700 mt-1">This revenue will not be treated as unrecorded cup sales and will not create estimated cup COGS.</p>
            </div>
          )}

          {showSettings && revenueType !== "other_revenue" && (
            <div className="border-t border-slate-200 pt-5 space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Sales Reconciliation Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Standard Cup Selling Price (RM)"><Input type="number" step="0.01" min="0" value={standardCupSellingPrice} onChange={(e) => setStandardCupSellingPrice(e.target.value)} className={inputClass} /></Field>
                <Field label="Conservative Cup Unit Cost (RM)"><Input type="number" step="0.01" min="0" value={conservativeCupUnitCost} onChange={(e) => setConservativeCupUnitCost(e.target.value)} className={inputClass} /></Field>
              </div>
              <Field label="Remark"><Textarea value={settingsRemark} onChange={(e) => setSettingsRemark(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-h-[60px]" /></Field>
              <p className="text-xs text-slate-500">A snapshot of these settings is saved with the event. Future changes will not affect previously saved events.</p>
            </div>
          )}

          {revenueType !== "other_revenue" && (productSalesCollected !== "" || products.length > 0) && (
            <div className="border-t border-slate-200 pt-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">Product Sales Reconciliation</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ReconRow label="Product Sales Collected" value={formatCurrency(totals.productRevenue)} />
                <ReconRow label="Recorded Product Sales" value={formatCurrency(totals.totalRecordedProductSales)} />
                <ReconRow label="Unrecorded Product Sales" value={formatCurrency(totals.unrecordedSales)} emphasized color={totals.unrecordedSales > 0 ? "text-[#fa9d1c]" : totals.unrecordedSales < 0 ? "text-red-600" : "text-emerald-700"} />
                <ReconRow label="Raw Cup Equivalent" value={`${totals.rawUnrecordedCups.toFixed(2)} cups`} />
                <ReconRow label="Estimated Whole Cups" value={`${totals.wholeCups} ${totals.wholeCups === 1 ? "cup" : "cups"}`} />
                <ReconRow label="Estimated Unrecorded COGS" value={formatCurrency(totals.estimatedUnrecordedCogs)} />
                <div className="sm:col-span-2 rounded-xl px-4 py-3 text-sm font-semibold bg-slate-100 text-slate-700 flex items-center justify-between gap-3"><span>Reconciliation Status</span><span>{totals.reconciliationStatus}</span></div>
              </div>
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-medium text-slate-800">Products Sold</h2>
            {revenueType !== "other_revenue" && (
              <Button type="button" onClick={() => setShowProductSelector(true)} className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full font-semibold h-11 px-5">
                <Plus className="w-4 h-4 mr-2" /> Add Product
              </Button>
            )}
          </div>

          {products.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <p className="text-slate-500">{revenueType === "other_revenue" ? "No products required" : "No products added yet"}</p>
              <p className="text-sm text-slate-400 mt-1">{revenueType === "other_revenue" ? "This Revenue Type is for income that did not come from product sales." : 'Tap "Add Product" to select from your product list'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {productCalculations.map((p) => (
                <div key={p.tempId} className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{p.productName}</p>
                      <p className="text-xs text-slate-500 mt-1">{p.category} · per {p.unit}</p>
                      {p.defaultPrice != null && <p className="text-xs font-semibold text-[#193f7d] mt-1">Selling Price: {formatCurrency(p.defaultPrice)} per {p.unit}</p>}
                    </div>
                    <Button type="button" onClick={() => setProducts((prev) => prev.filter((x) => x.tempId !== p.tempId))} variant="ghost" size="icon" className="text-red-500 rounded-full"><Trash2 className="w-4 h-4" /></Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="Quantity Sold"><Input type="number" min="0" step="0.01" value={p.quantitySold} onChange={(e) => changeProduct(p.tempId, "quantitySold", e.target.value)} className={`${inputClass} bg-white`} /></Field>
                    <Field label="Unit Cost (RM)"><Input type="number" min="0" step="0.01" value={p.unitCost} onChange={(e) => changeProduct(p.tempId, "unitCost", e.target.value)} className={`${inputClass} bg-white`} /></Field>
                    <Field label="Total Sales (RM)">
                      <Input type="number" min="0" step="0.01" value={p.totalSalesAmount} onChange={(e) => changeProduct(p.tempId, "totalSalesAmount", e.target.value)} className={`${inputClass} bg-white`} />
                      {!p.salesAutoCalc && p.defaultPrice != null && <button type="button" onClick={() => resetAutoCalc(p.tempId)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#193f7d] hover:underline"><RotateCcw className="w-3 h-3" /> Reset to Auto Calculation</button>}
                    </Field>
                  </div>

                  <Field label="Remark"><Input value={p.remark} onChange={(e) => changeProduct(p.tempId, "remark", e.target.value)} placeholder="Optional" className={`${inputClass} bg-white`} /></Field>
                  <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                    <Mini label="Product Cost" value={formatCurrency(p.productCostCalc)} />
                    <Mini label="Gross Profit" value={formatCurrency(p.productGrossProfitCalc)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-medium text-slate-800">Staff Wages</h2>
            <Button type="button" onClick={() => setStaffWages((prev) => [...prev, emptyStaff()])} variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Add Staff</Button>
          </div>

          {staffWages.length === 0 ? <div className="text-center py-6 bg-slate-50 rounded-xl text-sm text-slate-500">No staff added</div> : (
            <div className="space-y-3">
              {staffWages.map((s) => (
                <div key={s.tempId} className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Staff Name"><Input value={s.staffName} onChange={(e) => setStaffWages((prev) => prev.map((x) => x.tempId === s.tempId ? { ...x, staffName: e.target.value } : x))} className={`${inputClass} bg-white`} /></Field>
                    <Field label="Wage Amount (RM)"><Input type="number" min="0" step="0.01" value={s.wageAmount} onChange={(e) => setStaffWages((prev) => prev.map((x) => x.tempId === s.tempId ? { ...x, wageAmount: e.target.value } : x))} className={`${inputClass} bg-white`} /></Field>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1"><Field label="Remark"><Input value={s.remark} onChange={(e) => setStaffWages((prev) => prev.map((x) => x.tempId === s.tempId ? { ...x, remark: e.target.value } : x))} className={`${inputClass} bg-white`} /></Field></div>
                    <Button type="button" onClick={() => setStaffWages((prev) => prev.filter((x) => x.tempId !== s.tempId))} variant="ghost" size="icon" className="text-red-500 h-12 w-12"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
          <h2 className="text-lg font-medium text-slate-800">Other Event Costs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Manual Rental Cost (RM)"><Input type="number" min="0" step="0.01" value={rentalCost} onChange={(e) => setRentalCost(e.target.value)} className={inputClass} /><p className="text-xs text-slate-500 mt-2">Do not repeat rental already recorded as a Prepaid Event Rental.</p></Field>
            <Field label="Transport Cost (RM)"><Input type="number" min="0" step="0.01" value={transportCost} onChange={(e) => setTransportCost(e.target.value)} className={inputClass} /></Field>
            <Field label="Other Cost (RM)"><Input type="number" min="0" step="0.01" value={otherCost} onChange={(e) => setOtherCost(e.target.value)} className={inputClass} /></Field>
          </div>

          {isEditMode && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div><p className="text-xs font-bold uppercase tracking-wider text-blue-700">Linked Prepaid Event Rental</p><p className="text-xs text-blue-700 mt-1">Read-only. These amounts come from Transactions linked to this exact Event.</p></div>
                <p className="font-mono text-lg font-bold text-blue-900">{formatCurrency(totals.linkedRental)}</p>
              </div>
              {linkedPrepaidRentalTransactions.length > 0 && (
                <div className="space-y-2 border-t border-blue-200 pt-2">
                  {linkedPrepaidRentalTransactions.map((tx) => <div key={tx.id} className="flex justify-between gap-3 text-sm"><span className="text-blue-800">{tx.txDate}{tx.description ? ` · ${tx.description}` : ""}</span><span className="font-mono font-semibold text-blue-900">{formatCurrency(tx.amount)}</span></div>)}
                </div>
              )}
              <div className="border-t border-blue-200 pt-3 flex justify-between gap-4"><span className="text-sm font-semibold">Total Rental Used in P&amp;L</span><span className="font-mono font-bold">{formatCurrency(totals.rental)}</span></div>
            </div>
          )}

          {!isEditMode && <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-800">For rental paid before the event: save the Event as <strong>Draft</strong> first, then create a <strong>Prepaid Event Rental</strong> transaction linked to it. Do not enter the same amount again here.</div>}
          <Field label="Other Cost Remark"><Textarea value={otherCostRemark} onChange={(e) => setOtherCostRemark(e.target.value)} placeholder="e.g. Ice, parking, printing, electricity..." className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-h-[70px]" /></Field>
        </section>

        <section className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-5 sm:p-6 space-y-4 shadow-lg">
          <h2 className="text-lg font-medium text-white">Live P&amp;L Summary</h2>
          <div className="grid grid-cols-2 gap-4"><SummaryRow label="Total Event Days" value={totalEventDays} isText /><SummaryRow label="Recorded Qty Sold" value={totals.totalQuantity} isText /></div>
          <Divider />
          <SummaryGroup title="Revenue">
            <SummaryRow label="Product Revenue" value={formatCurrency(totals.productRevenue)} />
            <SummaryRow label="Other / Non-Product Revenue" value={formatCurrency(totals.otherRevenue)} />
            <SummaryRow label="Total Event Revenue" value={formatCurrency(totals.totalRevenue)} highlight />
          </SummaryGroup>
          {revenueType !== "other_revenue" && <><Divider /><SummaryGroup title="Product Sales Reconciliation"><SummaryRow label="Recorded Product Sales" value={formatCurrency(totals.totalRecordedProductSales)} /><SummaryRow label="Unrecorded Product Sales" value={formatCurrency(totals.unrecordedSales)} /><SummaryRow label="Estimated Unrecorded COGS" value={formatCurrency(totals.estimatedUnrecordedCogs)} /></SummaryGroup></>}
          <Divider />
          <SummaryGroup title="Cost of Goods Sold">
            <SummaryRow label="Recorded Product Cost" value={formatCurrency(totals.totalRecordedProductCost)} />
            <SummaryRow label="Estimated Unrecorded COGS" value={formatCurrency(totals.estimatedUnrecordedCogs)} />
            <SummaryRow label="Total COGS" value={formatCurrency(totals.totalCogs)} highlight />
            <SummaryRow label="Gross Profit" value={formatCurrency(totals.grossProfit)} highlight />
            <SummaryRow label="Gross Margin" value={`${totals.grossMargin.toFixed(2)}%`} />
          </SummaryGroup>
          <Divider />
          <SummaryGroup title="Event Expenses">
            <SummaryRow label="Rental" value={formatCurrency(totals.rental)} />
            <SummaryRow label="Staff Wages" value={formatCurrency(totals.totalStaff)} />
            <SummaryRow label="Transport" value={formatCurrency(totals.transport)} />
            <SummaryRow label="Other Event Cost" value={formatCurrency(totals.other)} />
            <SummaryRow label="Total Event Expenses" value={formatCurrency(totals.totalOpex)} highlight />
          </SummaryGroup>
          <Divider />
          <SummaryRow label="Net Profit / Loss" value={formatCurrency(totals.netProfit)} highlight />
          <SummaryRow label="Net Margin" value={`${totals.profitMargin.toFixed(2)}%`} highlight />
          <SummaryRow label="Avg Revenue / Day" value={formatCurrency(totals.avgSales)} />
          <SummaryRow label="Avg Net Profit / Day" value={formatCurrency(totals.avgNetProfit)} />
          <SummaryRow label="Reconciliation Status" value={totals.reconciliationStatus} isText />
        </section>

        <div className="flex gap-3 pt-2">
          {isEditMode && <Button type="button" onClick={() => navigate(`/events/${eventId}`)} variant="outline" className="flex-1 rounded-full h-12">Cancel</Button>}
          <Button type="button" onClick={handleSave} disabled={submitting} className="flex-1 bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-12 font-semibold">
            <Save className="w-5 h-5 mr-2" />
            {submitting ? "Saving..." : isEditMode ? "Update Event" : status === "draft" ? "Save Draft Event" : "Save Event"}
          </Button>
        </div>
      </div>

      <ProductSelectorDialog
        open={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        products={availableProducts}
        categories={categories}
        existingProductIds={products.map((p) => p.productId)}
        onSelect={addProduct}
      />
    </div>
  );
};

const Field = ({ label, children }) => <div><Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">{label}</Label>{children}</div>;
const Divider = () => <div className="h-px bg-white/20" />;
const SummaryGroup = ({ title, children }) => <div className="space-y-2"><p className="text-xs font-bold uppercase tracking-wider text-white/60">{title}</p>{children}</div>;
const SummaryRow = ({ label, value, highlight = false, isText = false }) => <div className="flex items-center justify-between gap-4"><span className="text-white/80 text-sm">{label}</span><span className={`${isText ? "" : "font-mono"} ${highlight ? "font-bold text-white" : "font-medium text-white/95"}`}>{value}</span></div>;
const ReconRow = ({ label, value, emphasized = false, color = "text-slate-900" }) => <div className="flex items-center justify-between gap-4 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2"><span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span><span className={`font-mono ${emphasized ? "font-bold" : "font-semibold"} ${color}`}>{value}</span></div>;
const Mini = ({ label, value }) => <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p><p className="font-mono font-semibold text-slate-900 mt-1">{value}</p></div>;

export default AddEvent;
